import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  PlayIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { getTrips, startTrip, type Trip } from '@/api/tripsApi'
import toast from 'react-hot-toast'

interface StartTripModalProps {
  isOpen: boolean
  onClose: () => void
  onTripStarted: (trip: Trip) => void
  preselectedTripId?: string
}

const StartTripModal: React.FC<StartTripModalProps> = ({
  isOpen,
  onClose,
  onTripStarted,
  preselectedTripId,
}) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState<'select' | 'confirm'>('select')

  // Data states
  const [trips, setTrips] = useState<Trip[]>([])

  // Selection states
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchData()
      setStep('select')
      resetSelections()
    }
  }, [isOpen])

  useEffect(() => {
    if (preselectedTripId && trips.length > 0) {
      const trip = trips.find(t => (t.id || (t as any)._id) === preselectedTripId)
      if (trip) {
        setSelectedTrip(trip)
      }
    }
  }, [preselectedTripId, trips])

  const resetSelections = () => {
    setSelectedTrip(null)
  }

  const fetchData = async () => {
    try {
      // Fetch trips assigned to this driver that are active (can be started)
      const tripsResponse = await getTrips({
        driverId: user?.id,
        status: 'active',
      })

      // Filter to only show trips that operate today
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
      const today = days[new Date().getDay()]
      const allTrips = tripsResponse.data?.trips || []

      const todaysTrips = allTrips.filter((trip: Trip) => {
        // Check if trip operates today
        if (trip.operatingDays && trip.operatingDays.length > 0) {
          return trip.operatingDays.includes(today as any)
        }
        return true // Include trips without specific operating days
      })

      setTrips(todaysTrips)

    } catch (error) {
      console.error('Failed to fetch data:', error)
      toast.error('Failed to load trip data')
    }
  }

  const handleStartTrip = async () => {
    // Driver must select an existing trip - they cannot create trips
    if (!selectedTrip) {
      toast.error('Please select a scheduled trip to start')
      return
    }

    setLoading(true)
    try {
      // Get the trip ID - handle both 'id' and '_id' from backend
      const tripId = selectedTrip.id || (selectedTrip as any)._id
      if (!tripId) {
        toast.error('Trip ID is missing')
        setLoading(false)
        return
      }

      // Start the existing trip
      const startResponse = await startTrip(tripId)

      toast.success('Trip started successfully!')
      onTripStarted(startResponse.data?.trip || selectedTrip)
      onClose()

    } catch (error: any) {
      console.error('Failed to start trip:', error)
      toast.error(error.response?.data?.message || 'Failed to start trip')
    } finally {
      setLoading(false)
    }
  }

  const renderSelectStep = () => (
    <div className="space-y-6">
      {/* Scheduled Trips Section */}
      {trips.length > 0 ? (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Today's Scheduled Trips</h3>
          <div className="space-y-2">
            {trips.map((trip) => {
              const tripId = trip.id || (trip as any)._id
              return (
              <button
                key={tripId}
                onClick={() => setSelectedTrip(trip)}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  (selectedTrip?.id || (selectedTrip as any)?._id) === tripId
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      trip.tripType === 'pickup' ? 'bg-blue-100' : 'bg-orange-100'
                    }`}>
                      <TruckIcon className={`h-5 w-5 ${
                        trip.tripType === 'pickup' ? 'text-blue-600' : 'text-orange-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{trip.tripName || trip.route?.routeName || 'Trip'}</p>
                      <p className="text-sm text-gray-500">
                        {trip.tripType === 'pickup' ? 'Morning Pickup' : 'Afternoon Dropoff'} • {trip.startTime}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{trip.studentCount || trip.enrolledStudents?.length || 0} students</p>
                    <p className="text-xs text-gray-500">{trip.vehicle?.vehicleNumber || 'No vehicle'}</p>
                  </div>
                </div>
              </button>
            )})}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <TruckIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No Trips Scheduled for Today</p>
          <p className="text-sm text-gray-500 mt-1">
            Contact your administrator to schedule trips for your routes.
          </p>
        </div>
      )}
    </div>
  )

  const renderConfirmStep = () => {
    if (!selectedTrip) return null

    return (
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <PlayIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-blue-900">Ready to Start Trip</p>
              <p className="text-sm text-blue-700">Please confirm the details below</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500">Trip Name</p>
              <p className="font-medium text-gray-900">{selectedTrip.tripName || selectedTrip.route?.routeName || 'N/A'}</p>
            </div>
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500">Trip Type</p>
              <p className="font-medium text-gray-900">
                {selectedTrip.tripType === 'pickup' ? 'Morning Pickup' : 'Afternoon Dropoff'}
              </p>
            </div>
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500">Students</p>
              <p className="font-medium text-gray-900">{selectedTrip.studentCount || selectedTrip.enrolledStudents?.length || 0}</p>
            </div>
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500">Vehicle</p>
              <p className="font-medium text-gray-900">
                {selectedTrip.vehicle?.vehicleNumber || 'Not assigned'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Important Reminders</p>
              <ul className="text-xs text-yellow-700 mt-1 space-y-1">
                <li>• GPS tracking will be enabled during the trip</li>
                <li>• Parents will be notified when students board/alight</li>
                <li>• Mark each student's attendance at their stop</li>
                <li>• Report any issues immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Start Trip</h2>
                  <p className="text-sm text-gray-500">
                    {step === 'select' && 'Select a trip to start'}
                    {step === 'confirm' && 'Confirm and start trip'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Step Indicator */}
              <div className="flex items-center mt-4 space-x-2">
                {['select', 'confirm'].map((s, index) => (
                  <React.Fragment key={s}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      step === s
                        ? 'bg-blue-600 text-white'
                        : index < ['select', 'confirm'].indexOf(step)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {index < ['select', 'confirm'].indexOf(step) ? (
                        <CheckCircleIcon className="h-5 w-5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    {index < 1 && (
                      <div className={`flex-1 h-1 rounded ${
                        index < ['select', 'confirm'].indexOf(step)
                          ? 'bg-green-500'
                          : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {step === 'select' && renderSelectStep()}
              {step === 'confirm' && renderConfirmStep()}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={() => {
                  if (step === 'confirm') setStep('select')
                  else onClose()
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {step === 'select' ? 'Cancel' : 'Back'}
              </button>

              {step === 'select' && (
                <button
                  onClick={() => setStep('confirm')}
                  disabled={!selectedTrip}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Continue
                  <svg className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {step === 'confirm' && (
                <button
                  onClick={handleStartTrip}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Starting...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4 mr-2" />
                      Start Trip
                    </>
                  )}
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default StartTripModal
